import React__default, { Component } from 'react';
import { __extends, __assign } from '../../_virtual/_tslib.js';
import { isAfter, isSameDay, differenceInMinutes } from 'date-fns';
import xor from 'lodash/xor';
import reduce from 'lodash/reduce';
import concat from 'lodash/concat';
import Loader from '../../components/Loader/index.js';
import withChat from '../Provider/withChat.js';
import { ChatActionsProvider } from '../ChatActions/index.js';
import Scrollable from '../Scrollable/index.js';
import Conversation$1 from '../../components/Conversation/index.js';
import ConversationQuery from '../ConversationQuery/index.js';
import TypingIndicator from '../TypingIndicator/index.js';
import Exchange from '../Exchange/index.js';
import MessageQuery from '../MessageQuery/index.js';
import { useStartGuestConversationMutation } from './StartGuestConversation.generated.js';
import startGuestConversation from '../../utils/startGuestConversation.js';

var ChunkMethod;

(function (ChunkMethod) {
  ChunkMethod[ChunkMethod["DATE"] = 0] = "DATE";
  ChunkMethod[ChunkMethod["IDLE_TIME"] = 1] = "IDLE_TIME";
})(ChunkMethod || (ChunkMethod = {}));

var ConversationContainer =
/** @class */
function (_super) {
  __extends(ConversationContainer, _super);

  function ConversationContainer(props) {
    var _this = _super.call(this, props) || this;

    _this.state = {
      queryCompleted: false
    };
    _this.scrollableElement = React__default.createRef();
    _this.toConversationItem = _this.toConversationItem.bind(_this);
    _this.handleQueryCompleted = _this.handleQueryCompleted.bind(_this);
    return _this;
  }

  ConversationContainer.prototype.componentDidMount = function () {
    if (this.props.guestChatEnabled) {
      startGuestConversation(this.props.startGuestConversation, this.props.conversation, this.props.recipientIds);
    }
  };

  ConversationContainer.prototype.componentDidUpdate = function (prevProps) {
    if (prevProps.chatClient !== this.props.chatClient || prevProps.conversation !== this.props.conversation || prevProps.authenticatedUserId !== this.props.authenticatedUserId || xor(prevProps.recipientIds || [], this.props.recipientIds || []).length > 0) {
      if (this.props.guestChatEnabled) {
        startGuestConversation(this.props.startGuestConversation, this.props.conversation, this.props.recipientIds);
      }
    }
  };

  ConversationContainer.prototype.getConversationRange = function (result) {
    var messages = result.messages;

    if (!messages || messages.length === 0) {
      return {
        start: null,
        end: new Date().toISOString()
      };
    }

    return {
      start: result.hasMore ? messages[messages.length - 1].sentAt.toDate().toISOString() : null,
      // Gets the most recent events (even if more recent than last message)
      end: null
    };
  };

  ConversationContainer.prototype.handleQueryCompleted = function (result) {
    if (result.messages) {
      // Fetch events that occured during this time range (the range from the oldest loaded message until now)
      var conversationRange = this.getConversationRange(result);

      if (this.props.fetchEvents) {
        this.props.fetchEvents(conversationRange.start, conversationRange.end);
      } // Determine the parent element to use as the scroll context (for manual scroll position correction)


      this.setState({
        queryCompleted: true
      });
    }
  };

  ConversationContainer.prototype.isMessageModel = function (arg) {
    return arg.read !== undefined;
  };

  ConversationContainer.prototype.toConversationItem = function (item) {
    if (this.isMessageModel(item)) {
      return {
        time: item.sentAt.toDate(),
        type: 'Message',
        message: item
      };
    } else {
      return {
        time: new Date(item.occuredAt),
        type: 'Event',
        event: item
      };
    }
  };
  /**
   * Merge Firebase conversation messages and user events into single sorted array
   *   of conversation items
   */


  ConversationContainer.prototype.mergeMessagesAndEvents = function (messages, events) {
    var _this = this;

    var conversationItems = concat(messages, events).reduce(function (acc, currentItem) {
      var newItem = _this.toConversationItem(currentItem);

      acc.push(newItem);
      return acc;
    }, []); // Sort array messages and events by date

    var sortedConversationItems = conversationItems.sort(function (a, b) {
      if (isAfter(a.time, b.time)) return -1;
      if (isAfter(b.time, a.time)) return 1;else return 0;
    });
    return sortedConversationItems;
  };
  /**
   * Given a list of conversation items, group them into "exchanges". Two chunking algoriths
   *   are supported:
   *  1) Chunking messages occuring in quick succession (i.e. no more than 5 minutes between any two items)
   *  2) Chunking messages by date
   *
   * Note: The conversation items MUST be sorted before chunking.
   */


  ConversationContainer.prototype.chunkConversationItems = function (conversationItems, chunkBy) {
    var IDLE_MINUTES = 5;
    return reduce(conversationItems, function (accumulatedExchanges, current, index, messages) {
      var previous = messages[index - 1]; // Create a new chunk if:
      //  - This is the first item
      //  - Or, we are chunking by date and the current item is a different date than the previous
      //  - Or, we are chunking by idle time and the current item is more than IDLE_MINUTES after the previous

      var isNewChunk = !previous || (chunkBy === ChunkMethod.DATE ? !isSameDay(previous.time, current.time) : Math.abs(differenceInMinutes(previous.time, current.time)) > IDLE_MINUTES);

      if (isNewChunk) {
        accumulatedExchanges.push([]);
      }

      accumulatedExchanges[accumulatedExchanges.length - 1].push(current);
      return accumulatedExchanges;
    }, []);
  };

  ConversationContainer.prototype.render = function () {
    var _this = this;

    var conversationId = this.props.conversation && this.props.conversation.conversationId;
    var conversationLoading = this.props.loading;
    /**
     * Once events are loaded they will be in the form Event[]
     * Events are fetched on every new message, but we want to only show the loader during the initial query
     * */

    var eventsLoading = this.props.eventsLoading && typeof this.props.events === 'undefined' ? true : false;
    return React__default.createElement(ChatActionsProvider, {
      messageAction: this.props.messageAction
    }, React__default.createElement(Scrollable, {
      element: this.scrollableElement,
      conversationId: conversationId
    }, React__default.createElement(MessageQuery, {
      conversationId: conversationId,
      onCompleted: this.handleQueryCompleted
    }, function (_a) {
      var messagesLoading = _a.loading,
          messages = _a.messages,
          hasMore = _a.hasMore,
          fetchMore = _a.fetchMore;
      var loading = conversationLoading || messagesLoading || eventsLoading;
      var exchanges = [];

      if (messages) {
        var conversationItems = _this.mergeMessagesAndEvents(messages, _this.props.events || []);

        exchanges = _this.chunkConversationItems(conversationItems, ChunkMethod.DATE);
      } // Render children if the conversation doesn't exist, or if we've reached the top of the conversation


      var showConversationStarter = !conversationId || !hasMore && !loading;
      return React__default.createElement(Conversation$1, {
        loading: loading,
        hasMore: hasMore || false,
        onInfiniteLoad: fetchMore,
        scrollableElementRef: _this.scrollableElement,
        onClick: _this.props.onClick,
        className: _this.props.conversationClassName
      }, showConversationStarter && _this.props.children, loading && React__default.createElement(Loader, {
        size: "22px",
        color: "#087bcc",
        thickness: "3px"
      }), exchanges && !eventsLoading && exchanges.reverse().map(function (exchange) {
        // Key by the last item in the exchange so that exchanges are stable
        //   even if older items are loaded later
        var lastItem = exchange[exchange.length - 1];
        return React__default.createElement(Exchange, {
          items: exchange,
          showRealSender: _this.props.showRealSender,
          enableReadReceipts: _this.props.enableReadReceipts,
          key: lastItem.type === 'Message' ? "exchange:message:" + lastItem.message.messageId : "exchange:event:" + lastItem.event.occuredAt
        });
      }), _this.props.bottomBanner && _this.props.bottomBanner(), conversationId && React__default.createElement(TypingIndicator, {
        conversationId: conversationId
      }));
    })));
  };

  return ConversationContainer;
}(Component);

var ConversationWithMutation = function ConversationWithMutation(props) {
  var startGuestConversation = useStartGuestConversationMutation()[0];
  return React__default.createElement(ConversationQuery, {
    conversationId: props.conversationId,
    recipientIds: props.guestChatEnabled ? props.recipientIds : undefined
  }, function (_a) {
    var loading = _a.loading,
        conversation = _a.conversation;
    return React__default.createElement(ConversationContainer, __assign({
      startGuestConversation: startGuestConversation,
      conversation: conversation,
      loading: loading
    }, props));
  });
};

var Conversation = withChat(ConversationWithMutation);

export default Conversation;
//# sourceMappingURL=index.js.map
